import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import client from '../lib/client';
import { openOAuthPopup, waitForOAuthResponse } from '../utils/oauth-helpers';
export const useOAuthMutations = () => {
    const { user } = useAuth();
    const [connectionStates, setConnectionStates] = useState({});
    const mapToOAuthCredentialType = (credentialType) => {
        const credentialTypeMap = {
            Atlassian: 'AtlassianOauth',
            GitHub: 'GitHubOauth',
            Slack: 'SlackOauth',
            Google: 'GoogleOauth',
            Microsoft: 'MicrosoftOauth',
            Dropbox: 'DropboxOauth',
        };
        return credentialTypeMap[credentialType] || credentialType;
    };
    const _generateOAuthUrl = async (request) => {
        if (!user?.access_token) {
            throw new Error('User not authenticated');
        }
        const response = await client.post('/v1/OAuth/generateUrl', {
            credentialType: mapToOAuthCredentialType(request.credentialType),
            scopes: request.scopes || '',
            domain: request.domain,
            useChatRedirectUrl: true,
            name: `office-${request.provider.toLowerCase()}-${Date.now()}`,
        }, {
            headers: {
                Authorization: `Bearer ${user.access_token}`,
            },
        });
        return response.data.authUrl || response.data.url;
    };
    const exchangeOAuthToken = async (request) => {
        if (!user?.access_token) {
            throw new Error('User not authenticated');
        }
        const requestBody = {
            useChatRedirectUrl: true,
            codeVerifier: request.codeVerifier ?? '',
            authCode: request.authCode,
            credentialType: mapToOAuthCredentialType(request.credentialType),
            name: `office-${request.provider.toLowerCase()}-${Date.now()}`,
        };
        const response = await client.post('/v1/oauth/exchangeOAuthToken', requestBody, {
            headers: {
                Authorization: `Bearer ${user.access_token}`,
            },
        });
        return response.data;
    };
    const createCredential = async (credentialData) => {
        if (!user?.access_token) {
            throw new Error('User not authenticated');
        }
        const response = await client.post('/v1/Credentials', credentialData, {
            headers: {
                Authorization: `Bearer ${user.access_token}`,
            },
        });
        return response.data;
    };
    const connectToProvider = async (provider, credentialType, integrationId, scopes, domain) => {
        try {
            setConnectionStates((prev) => ({ ...prev, [integrationId]: true }));
            // Generate OAuth URL and get the full response
            const oauthUrlResponse = await client.post('/v1/OAuth/generateUrl', {
                credentialType: mapToOAuthCredentialType(credentialType),
                scopes: scopes || '',
                domain: domain,
                useChatRedirectUrl: true,
                name: `office-${provider.toLowerCase()}-${Date.now()}`,
            }, {
                headers: {
                    Authorization: `Bearer ${user?.access_token}`,
                },
            });
            const authUrl = oauthUrlResponse.data.authUrl || oauthUrlResponse.data.url;
            // Open popup and wait for response
            const popup = openOAuthPopup(authUrl);
            const oauthResponse = await waitForOAuthResponse(popup);
            // Add the code verifier from the initial OAuth response
            oauthResponse.codeVerifier = oauthUrlResponse.data.codeVerifier;
            // Exchange code for token
            if (oauthResponse.code) {
                const tokenData = await exchangeOAuthToken({
                    authCode: oauthResponse.code,
                    credentialType,
                    provider,
                    integrationId,
                    codeVerifier: oauthResponse.codeVerifier || '', // Make sure it's not undefined
                });
                // Convert token data to credential format (following airia-chat-web pattern)
                const credData = tokenData
                    ? [
                        { key: 'accessToken', value: tokenData.accessToken || '' },
                        { key: 'refreshToken', value: tokenData.refreshToken || '' },
                    ]
                    : [];
                // Get the user ID from the Profile endpoint
                let actualUserId = '';
                try {
                    const profileResponse = await client.get(`/v1/Profile`, {
                        headers: {
                            Authorization: `Bearer ${user?.access_token}`,
                        },
                    });
                    actualUserId = profileResponse.data.userId || profileResponse.data.id;
                }
                catch (profileError) {
                    console.warn('Could not fetch profile for credential creation, using token sub:', profileError);
                    actualUserId = user?.profile?.sub || 'unknown';
                }
                // Create credential in Airia system
                await createCredential({
                    name: `office-${provider.toLowerCase()}-${actualUserId}-${Date.now()}`,
                    administrativeScope: 'Personal',
                    type: mapToOAuthCredentialType(credentialType),
                    credentialData: [
                        ...credData.filter((item) => item.key !== 'idToken'),
                        {
                            key: 'scope',
                            value: scopes || '',
                        },
                    ],
                });
            }
            else if (oauthResponse.error) {
                console.error('🔗 OAuth response contains error:', oauthResponse);
                throw new Error(oauthResponse.error_description || oauthResponse.error);
            }
            else {
                console.error('🔗 OAuth response missing code:', oauthResponse);
                throw new Error('OAuth flow cancelled or failed');
            }
        }
        catch (error) {
            console.error(`Failed to connect to ${provider}:`, error);
            throw error;
        }
        finally {
            setConnectionStates((prev) => ({ ...prev, [integrationId]: false }));
        }
    };
    return {
        connectToProvider,
        connectionStates,
    };
};
